import { createSlice } from '@reduxjs/toolkit'
import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import type { IConfig } from '../domain/types'
import type { ICheckoutRules, SessionDataArgs } from '../domain/types/session'
import { resolveHidePrices } from '../domain/hidePrices'
import castSessionNotifications from '../domain/castNotifications'
import type { ConfigState, LoaderConfig } from './configTypes'
import { ErrorMessageTranslation } from './configTypes'

export const initialState: ConfigState = {
  loaders: [],
  loading: true,
  config: undefined,
  loadingDelay: 1000,
  sessionId: undefined,
  storeName: undefined,
  errorMessage: null,
  globalError: false,
  showErrorTitle: false,
  params: {},
  showRecommendations: false,
  showSuggestedItems: false,
  sessionNotifications: {
    STOCK_AVAILABLE: false,
    AUTHENTICATION_NEEDED: false,
    SESSION_CREATED: false,
    ORDER_REMINDER: false,
    ABANDONED_CART: false,
  },
}

/**
 * `hidePricesActive` is derived, not authored: it depends on the storefront's
 * `hidePrices` rule *and* on this customer's custom fields. Either can arrive
 * first, so both `setConfig` and `setSessionData` re-derive it.
 */
const deriveHidePricesActive = (state: Draft<ConfigState>): void => {
  if (state.config?.options) {
    state.config.options.hidePricesActive = resolveHidePrices(
      state.config.options.hidePrices,
      state.sessionData?.customer?.customFields,
    )
  }
}

export const configSlice = createSlice({
  name: 'configSlice',
  initialState,
  reducers: {
    /**
     * Replaces cinnamon's `fetchConfig` async thunk. There the slice owned the
     * HTTP call, ran the `{{variable}}` interpreter over the response and
     * reported to DataDog — none of which core can or should do. The host app
     * loads and validates the config (zod, `@commerce/tokens`) and hands the
     * result in. See docs/NOT_PORTED.md.
     */
    setConfig: (state, { payload }: PayloadAction<IConfig>) => {
      state.config = payload as Draft<IConfig>
      deriveHidePricesActive(state)
    },
    manageErrors: (
      state,
      {
        payload: { errorMessageTranslation, globalError, showErrorTitle },
      }: PayloadAction<{
        errorMessageTranslation: ErrorMessageTranslation
        globalError?: boolean
        showErrorTitle?: boolean
      }>,
    ) => {
      let errorMessage: string = 'An error has occurred'
      const errorsTexts = state.config?.texts?.errors
      if (errorMessageTranslation) {
        switch (errorMessageTranslation) {
          case ErrorMessageTranslation.CatalogueEmptyData:
            errorMessage =
              (errorsTexts?.catalogueEmptyData as string) || 'Catalogue empty'
            break
          case ErrorMessageTranslation.InitialData:
            errorMessage =
              (errorsTexts?.initialData as string) || 'Session expired'
            break
          case ErrorMessageTranslation.GetCart:
            errorMessage =
              (errorsTexts?.getCart as string) || 'Error on get cart'
            break
          case ErrorMessageTranslation.NonProvidedParams:
            errorMessage =
              (errorsTexts?.nonProvidedParams as string) || 'No params provided'
            break
          case ErrorMessageTranslation.ErrorOnInitialLoad:
            errorMessage =
              errorsTexts?.tryAgainIntruction || 'An error has ocurred'
            break
          default:
            break
        }
      }
      state.errorMessage = errorMessage
      state.globalError = !!globalError
      state.showErrorTitle = !!showErrorTitle
    },
    addLoader: (state, action: PayloadAction<LoaderConfig>) => {
      const { payload } = action
      if (typeof payload === 'string') {
        state.loaders = [...state.loaders, payload]
      } else {
        state.loaders = [...state.loaders, payload.name]
        state.loadingDelay = payload.delay
      }
      state.loading = true
    },
    removeLoader: (state, action: PayloadAction<string>) => {
      const index = state.loaders.indexOf(action.payload)
      const newLoaders = [...state.loaders]
      newLoaders.splice(index, 1)
      state.loaders = newLoaders
      state.loading = newLoaders.length > 0
    },
    setSessionData: (state, action: PayloadAction<SessionDataArgs>) => {
      const { payload } = action
      if (payload?.configuration?.notifications) {
        state.sessionNotifications = castSessionNotifications(
          payload?.configuration?.notifications,
        )
      }
      state.sessionData = payload
      deriveHidePricesActive(state)
      const { frequentlyBoughtTogether, suggestedItems } =
        payload.configuration?.recommendedProducts || {}
      const showRecommendations = !!payload.configuration?.showRecommendations
      const showFrequentlyBoughtTogetherItems =
        frequentlyBoughtTogether === 'ML' || showRecommendations
      const showSuggestedItems = suggestedItems === 'ML' || showRecommendations
      state.showRecommendations = showFrequentlyBoughtTogetherItems
      state.showSuggestedItems = showSuggestedItems
    },
    setCheckoutRules: (state, { payload }: PayloadAction<ICheckoutRules>) => {
      if (state?.sessionData?.configuration?.checkoutRules && payload) {
        state.sessionData.configuration.checkoutRules = {
          ...state.sessionData.configuration.checkoutRules,
          ...payload,
        }
      }
    },
    setSessionId: (state, { payload }: PayloadAction<string>) => {
      state.sessionId = payload
    },
    updateParams: (state, { payload }: PayloadAction<ConfigState['params']>) => {
      state.params = payload
    },
    setStoreName: (state, action: PayloadAction<string>) => {
      state.storeName = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.secondaryLoader = action.payload
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
  },
})

export const {
  setConfig,
  setSessionId,
  updateParams,
  setStoreName,
  setSessionData,
  setLoading,
  setErrorMessage,
  addLoader,
  removeLoader,
  manageErrors,
  setCheckoutRules,
} = configSlice.actions

export default configSlice.reducer
